import {
  type AnnulOperation,
  type ConfigurationOperation,
  isAnnulOperation,
  isParameterDefinitionCreateOperation,
  isReconciliationSpecCreateOperation,
  isTemplateCreateOperation,
  isTemplateHideOperation,
  type ParameterDefinitionCreateOperation,
  type ParameterValueAmendOperation,
  type ParameterValueCreateOperation,
  type ReconciliationSpecCreateOperation,
  type TemplateCreateOperation,
  type TemplateHideOperation,
  type TemplateInOut,
  type WorkspaceOperationInOut,
} from '@festina/configuration-general-types/configuration'
import {
  type EnumDefinitionCreateOperation,
  type EnumLiteralCreateOperation,
  isEnumDefinitionCreateOperation,
  isEnumLiteralCreateOperation,
  isParameterValueAmendOperation,
  isParameterValueCreateOperation,
  isRegisteredTenantCreateOperation,
  type RegisteredTenantCreateOperation,
} from '@festina/life-general-types/life'
import type { Merge, Optional } from '@festina-life/core'

import type { TemplateVersionInfo } from '../../types/templateModel'
import { type GlMapOperation, isGlMapOperation } from '../glMapOperations/types'
import type {
  Enriched,
  IndexedOperations,
  OperationWithSourceInfo,
  WorkspaceOperationOf,
} from '../types'

export type IndexedTemplateOperationWithMetaData =
  OperationWithSourceInfo<TemplateOperation>

export type IndexedTemplateOperations = IndexedOperations<TemplateOperation> & {
  /**
   * Non-empty templateId provided for convenience.
   */
  templateId: string
  /**
   * In out entity from the last create operation, if any exists.
   */
  lastEntity: Optional<TemplateInOut>
}

export type InheritableField = { path: string; origin?: Optional<string> }

export const isInherited = <T extends InheritableField>(value: T) =>
  !!value.origin && value.origin !== value.path

export const isNotInherited = <T extends InheritableField>(value: T) =>
  !isInherited(value)

export type EnrichedTemplateCreateOperationWithVersionInfo = Merge<
  Enriched<TemplateCreateOperation>,
  { versionInfo: TemplateVersionInfo }
>

export type ShadowedOperationsPerTemplateIdAndDate = Map<
  string,
  Map<string | null, IndexedTemplateOperationWithMetaData[]>
>

export type TemplateOperation = TemplateCreateOperation | TemplateHideOperation

export const isTemplateOperation = (
  operation: ConfigurationOperation,
): operation is TemplateOperation =>
  isTemplateCreateOperation(operation) || isTemplateHideOperation(operation)

export const isWorkspaceGlMapOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<GlMapOperation> =>
  !!operation.operation && isGlMapOperation(operation.operation)

export const isWorkspaceReconciliationSpecCreateOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<ReconciliationSpecCreateOperation> =>
  !!operation.operation &&
  isReconciliationSpecCreateOperation(operation.operation)

export const isWorkspaceEnumLiteralOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<EnumLiteralCreateOperation> =>
  !!operation.operation && isEnumLiteralCreateOperation(operation.operation)

export const isWorkspaceTemplateOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<TemplateOperation> =>
  !!operation.operation && isTemplateOperation(operation.operation)

export const isWorkspaceTemplateCreateOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<TemplateCreateOperation> =>
  !!operation.operation && isTemplateCreateOperation(operation.operation)

export const isWorkspaceParameterDefinitionOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<ParameterDefinitionCreateOperation> =>
  !!operation.operation &&
  isParameterDefinitionCreateOperation(operation.operation)

export const isWorkspaceAnnulOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<AnnulOperation> =>
  !!operation.operation && isAnnulOperation(operation.operation)

export const isWorkspaceParameterValueOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<ParameterValueCreateOperation> =>
  !!operation.operation && isParameterValueCreateOperation(operation.operation)

export const isWorkspaceParameterValueAmendOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<ParameterValueAmendOperation> =>
  !!operation.operation && isParameterValueAmendOperation(operation.operation)

export const isWorkspaceRegisteredTenantOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<RegisteredTenantCreateOperation> =>
  !!operation.operation &&
  isRegisteredTenantCreateOperation(operation.operation)

export const isWorkspaceEnumDefinitionOperation = (
  operation: WorkspaceOperationInOut,
): operation is WorkspaceOperationOf<EnumDefinitionCreateOperation> =>
  !!operation.operation && isEnumDefinitionCreateOperation(operation.operation)

export function assertTemplateCreateOperation<T extends ConfigurationOperation>(
  operation: T,
): asserts operation is T & TemplateCreateOperation {
  if (!isTemplateCreateOperation(operation)) {
    throw new Error('Operation is not a TemplateCreateOperation')
  }
}
